// 对齐/分布/阵列纯函数（基于包围盒计算，无渲染依赖，便于单测）
// 业务：机房机柜/RTU 批量排版 —— 底部贴地对齐、横向均分、矩阵阵列

export interface Vec3 { x: number; y: number; z: number; }

export interface LayoutBox {
  min: Vec3;
  max: Vec3;
}

export interface LayoutItem {
  id: string;
  position: [number, number, number];
  /** 世界包围盒（由调用方从 modelRefsRegistry 计算，缺省用 position 单点） */
  box?: LayoutBox;
}

export type AlignType =
  | 'x-left' | 'x-center' | 'x-right'
  | 'z-front' | 'z-center' | 'z-back'
  | 'y-bottom' | 'y-center' | 'y-top';

export type DistributeType = 'x-even' | 'z-even' | 'y-even';

export const ALIGN_LABEL: Record<AlignType, string> = {
  'x-left': 'X 左对齐',
  'x-center': 'X 水平居中',
  'x-right': 'X 右对齐',
  'z-front': 'Z 前对齐',
  'z-center': 'Z 纵深居中',
  'z-back': 'Z 后对齐',
  'y-bottom': 'Y 底部贴地',
  'y-center': 'Y 垂直居中',
  'y-top': 'Y 顶部对齐',
};

export const DISTRIBUTE_LABEL: Record<DistributeType, string> = {
  'x-even': 'X 水平均分',
  'z-even': 'Z 纵深均分',
  'y-even': 'Y 垂直均分',
};

// ================= 包围盒工具 =================

function boxOf(item: LayoutItem): LayoutBox {
  if (item.box) return item.box;
  const p = item.position;
  return { min: { x: p[0], y: p[1], z: p[2] }, max: { x: p[0], y: p[1], z: p[2] } };
}

function centerOf(box: LayoutBox): Vec3 {
  return {
    x: (box.min.x + box.max.x) / 2,
    y: (box.min.y + box.max.y) / 2,
    z: (box.min.z + box.max.z) / 2,
  };
}

function sizeOf(box: LayoutBox): Vec3 {
  return {
    x: box.max.x - box.min.x,
    y: box.max.y - box.min.y,
    z: box.max.z - box.min.z,
  };
}

export function getTotalBox(items: LayoutItem[]): LayoutBox {
  const total: LayoutBox = {
    min: { x: Infinity, y: Infinity, z: Infinity },
    max: { x: -Infinity, y: -Infinity, z: -Infinity },
  };
  items.forEach((item) => {
    const b = boxOf(item);
    total.min.x = Math.min(total.min.x, b.min.x);
    total.min.y = Math.min(total.min.y, b.min.y);
    total.min.z = Math.min(total.min.z, b.min.z);
    total.max.x = Math.max(total.max.x, b.max.x);
    total.max.y = Math.max(total.max.y, b.max.y);
    total.max.z = Math.max(total.max.z, b.max.z);
  });
  return total;
}

// ================= 一、对齐（9 向） =================

export function alignItems(items: LayoutItem[], align: AlignType): Map<string, [number, number, number]> {
  if (items.length < 2) return new Map();
  const total = getTotalBox(items);
  const result = new Map<string, [number, number, number]>();

  items.forEach((item) => {
    const b = boxOf(item);
    const c = centerOf(b);
    const s = sizeOf(b);
    const pos: [number, number, number] = [...item.position];

    switch (align) {
      // X 轴
      case 'x-left': pos[0] = total.min.x + s.x / 2; break;
      case 'x-center': pos[0] = (total.min.x + total.max.x) / 2; break;
      case 'x-right': pos[0] = total.max.x - s.x / 2; break;
      // Z 轴（前后）
      case 'z-front': pos[2] = total.min.z + s.z / 2; break;
      case 'z-center': pos[2] = (total.min.z + total.max.z) / 2; break;
      case 'z-back': pos[2] = total.max.z - s.z / 2; break;
      // Y 轴（底部贴地核心）
      case 'y-bottom': pos[1] = total.min.y + s.y / 2; break;
      case 'y-center': pos[1] = (total.min.y + total.max.y) / 2; break;
      case 'y-top': pos[1] = total.max.y - s.y / 2; break;
    }
    // 若单点（无 box），中心即位置，直接对齐
    if (!item.box) {
      if (align === 'x-left') pos[0] = total.min.x;
      if (align === 'x-right') pos[0] = total.max.x;
      if (align === 'z-front') pos[2] = total.min.z;
      if (align === 'z-back') pos[2] = total.max.z;
      if (align === 'y-bottom') pos[1] = total.min.y;
      if (align === 'y-top') pos[1] = total.max.y;
    }
    result.set(item.id, pos);
  });
  return result;
}

// ================= 二、均匀分布（3 轴） =================

export function distributeItems(items: LayoutItem[], dist: DistributeType): Map<string, [number, number, number]> {
  if (items.length < 3) return new Map();
  const result = new Map<string, [number, number, number]>();
  const total = getTotalBox(items);

  // 按对应轴中心排序
  const sorted = [...items].sort((a, b) => {
    const ca = centerOf(boxOf(a));
    const cb = centerOf(boxOf(b));
    const va = dist === 'x-even' ? ca.x : dist === 'z-even' ? ca.z : ca.y;
    const vb = dist === 'x-even' ? cb.x : dist === 'z-even' ? cb.z : cb.y;
    return va - vb;
  });

  const axisMin = dist === 'x-even' ? total.min.x : dist === 'z-even' ? total.min.z : total.min.y;
  const axisMax = dist === 'x-even' ? total.max.x : dist === 'z-even' ? total.max.z : total.max.y;
  const step = (axisMax - axisMin) / (items.length - 1);

  sorted.forEach((item, idx) => {
    const b = boxOf(item);
    const s = sizeOf(b);
    const c = centerOf(b);
    const targetCenter = axisMin + step * idx;
    const pos: [number, number, number] = [...item.position];
    if (dist === 'x-even') pos[0] += targetCenter - c.x;
    if (dist === 'z-even') pos[2] += targetCenter - c.z;
    if (dist === 'y-even') pos[1] += targetCenter - c.y;
    result.set(item.id, pos);
  });
  return result;
}

// ================= 三、矩阵阵列（批量机柜/RTU） =================

export interface MatrixConfig {
  cols: number;
  rows: number;
  gapX: number;
  gapZ: number;
}

export function matrixOffsets(source: LayoutItem, config: MatrixConfig): Array<[number, number, number]> {
  const b = boxOf(source);
  const s = sizeOf(b);
  const cellX = s.x + config.gapX;
  const cellZ = s.z + config.gapZ;
  const offsets: Array<[number, number, number]> = [];
  for (let r = 0; r < config.rows; r++) {
    for (let c = 0; c < config.cols; c++) {
      if (r === 0 && c === 0) continue; // 源模型本身
      offsets.push([source.position[0] + c * cellX, source.position[1], source.position[2] + r * cellZ]);
    }
  }
  return offsets;
}